import { analytics } from "@/lib/analytics";
import { reportUnexpectedError } from "@/lib/errors";
import { useCartContext } from "@/providers/cart-provider";
import { toast } from "@medusajs/ui";
import { useNavigate } from "@tanstack/react-router";
import plur from "plur";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { ProductDetailDialog } from "./ProductDetailDialog";

export interface ProductDetailDialogData {
  offerId: string;
  sellerName: string;
  productName: string;
  thumbnail: string | null;
  // Multi-variant mode: when a product_type uses the variant grid, these are
  // set so the dialog can load all variants for the locked supplier.
  productId?: string;
  productType?: string;
  sellerId?: string;
  // Single-offer mode: the offer's product, to price a claimed seller's offer
  // (KAN-343). Separate from `productId`, which means "variant grid mode".
  pricingProductId?: string;
}

/** A single variant order line to add to the cart. */
export interface VariantOrderItem {
  offerId: string;
  quantity: number;
}

interface ProductDetailDialogContextValue {
  openDialog: (data: ProductDetailDialogData) => void;
}

const ProductDetailDialogContext =
  createContext<ProductDetailDialogContextValue | null>(null);

interface ProductDetailDialogProviderProps {
  children: ReactNode;
}

export function ProductDetailDialogProvider({
  children
}: ProductDetailDialogProviderProps) {
  const { addToCart, isAddingToCart } = useCartContext();
  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    data: ProductDetailDialogData | null;
  }>({
    open: false,
    data: null
  });

  const openDialog = useCallback((data: ProductDetailDialogData) => {
    // Funnel step 3. Browsing here opens a dialog rather than navigating to a
    // PDP, so there is no route change to infer a product view from - without
    // this the step between "searched" and "added to cart" is invisible.
    //
    // Fired on the open call, not from the dialog's mount: re-opening the same
    // product is a genuine second view, and this is the one place every entry
    // point (preview card, price-comparison card, shade matrix) funnels through.
    analytics.productViewed({
      offer_id: data.offerId,
      // Only the shade-matrix flow knows these before the dialog loads.
      product_id: data.productId ?? null,
      seller_id: data.sellerId ?? null,
      multi_variant: Boolean(data.productId)
    });

    setDialogState({ open: true, data });
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setDialogState((prev) => ({ ...prev, open }));
  }, []);

  const handleAddToCart = useCallback(
    async (offerId: string, quantity: number) => {
      if (isAddingToCart) return;

      try {
        await addToCart({ offer_id: offerId, quantity });
        toast.success("Added to cart", {
          description: `${quantity} ${plur("item", quantity)} added to your cart`
        });
        // Close dialog after successful add
        setDialogState((prev) => ({ ...prev, open: false }));
      } catch (error) {
        console.error("Failed to add to cart:", error);
        reportUnexpectedError(error, "product dialog add to cart");
        toast.error("Failed to add to cart", {
          description: "Please try again"
        });
      }
    },
    [addToCart, isAddingToCart]
  );

  const handleQuickBuy = useCallback(
    async (offerId: string, quantity: number) => {
      if (isAddingToCart) return;

      // Quick buy: add to the seller's cart, then go straight to checkout for
      // that one cart. No "Added to cart" toast - the navigation is the feedback,
      // and the buyer is deliberately skipping the cart page.
      try {
        const { cart_id } = await addToCart({
          offer_id: offerId,
          quantity,
          quickBuy: true
        });
        setDialogState((prev) => ({ ...prev, open: false }));
        await navigate({
          to: "/checkout",
          search: { cart_ids: cart_id, step: "information" }
        });
      } catch (error) {
        console.error("Failed to quick buy:", error);
        reportUnexpectedError(error, "product dialog quick buy");
        toast.error("Couldn't start checkout", {
          description: "Please try again"
        });
      }
    },
    [addToCart, isAddingToCart, navigate]
  );

  // Multi-variant: add several variant offers to the locked seller's cart.
  // No bulk endpoint exists yet, so items are added sequentially; feedback is
  // aggregated into a single toast and the dialog stays open on partial failure.
  const handleAddOffersToCart = useCallback(
    async (items: VariantOrderItem[]) => {
      if (isAddingToCart) return;

      const toAdd = items.filter((item) => item.quantity > 0);
      if (toAdd.length === 0) return;

      const failedCount = await toAdd.reduce(async (accPromise, item) => {
        const acc = await accPromise;
        try {
          await addToCart({ offer_id: item.offerId, quantity: item.quantity });
          return acc;
        } catch (error) {
          console.error("Failed to add variant to cart:", error);
          reportUnexpectedError(error, "product dialog add variants to cart");
          return acc + 1;
        }
      }, Promise.resolve(0));

      const addedCount = toAdd.length - failedCount;

      if (addedCount > 0) {
        toast.success("Added to cart", {
          description: `${addedCount} ${plur("item", addedCount)} added to your cart`
        });
      }

      if (failedCount > 0) {
        toast.error("Some items couldn't be added", {
          description: `${failedCount} ${plur("item", failedCount)} failed. Please try again.`
        });
        // Keep the dialog open so the user can retry the failed items.
        return;
      }

      setDialogState((prev) => ({ ...prev, open: false }));
    },
    [addToCart, isAddingToCart]
  );

  // Multi-variant quick buy: same sequential add, but on full success skip the
  // toast and go straight to checkout for the locked seller's cart. On any
  // failure stay on the dialog - checking out a partial order unannounced would
  // be worse than asking the buyer to look again.
  const handleQuickBuyOffers = useCallback(
    async (items: VariantOrderItem[]) => {
      if (isAddingToCart) return;

      const toAdd = items.filter((item) => item.quantity > 0);
      if (toAdd.length === 0) return;

      let cartId: string | undefined;
      let failedCount = 0;
      for (const item of toAdd) {
        try {
          const res = await addToCart({
            offer_id: item.offerId,
            quantity: item.quantity,
            quickBuy: true
          });
          cartId = res.cart_id;
        } catch (error) {
          console.error("Failed to add variant to cart:", error);
          reportUnexpectedError(error, "product dialog quick buy variants");
          failedCount += 1;
        }
      }

      if (failedCount > 0 || !cartId) {
        toast.error("Some items couldn't be added", {
          description: `${failedCount} ${plur("item", failedCount)} failed. Check your cart before checking out.`
        });
        return;
      }

      setDialogState((prev) => ({ ...prev, open: false }));
      await navigate({
        to: "/checkout",
        search: { cart_ids: cartId, step: "information" }
      });
    },
    [addToCart, isAddingToCart, navigate]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      openDialog
    }),
    [openDialog]
  );

  return (
    <ProductDetailDialogContext.Provider value={contextValue}>
      {children}
      {dialogState.data && (
        <ProductDetailDialog
          open={dialogState.open}
          onOpenChange={handleOpenChange}
          offerId={dialogState.data.offerId}
          sellerName={dialogState.data.sellerName}
          productName={dialogState.data.productName}
          thumbnail={dialogState.data.thumbnail}
          productId={dialogState.data.productId}
          productType={dialogState.data.productType}
          sellerId={dialogState.data.sellerId}
          pricingProductId={dialogState.data.pricingProductId}
          onAddToCart={handleAddToCart}
          onQuickBuy={handleQuickBuy}
          onAddOffersToCart={handleAddOffersToCart}
          onQuickBuyOffers={handleQuickBuyOffers}
          isAddingToCart={isAddingToCart}
        />
      )}
    </ProductDetailDialogContext.Provider>
  );
}

export function useProductDetailDialog(): ProductDetailDialogContextValue {
  const context = useContext(ProductDetailDialogContext);
  if (!context) {
    throw new Error(
      "useProductDetailDialog must be used within a ProductDetailDialogProvider"
    );
  }
  return context;
}
